import { JSConn } from "@/utilities/js-conn";

import LoadingAnimation from "@/components/LoadingAnimation/LoadingAnimation.vue";
import ListPageHeader from "@/components/ListPageHeader/ListPageHeader.vue";

import { format } from "date-fns";
import { TOAST_OPTIONS } from "@/constants";

import {
  ref,
  onMounted,
  defineComponent,
  computed,
} from "@vue/composition-api";
import { itemRouteBuilder, NamedRts } from "@/router";

import { genericSort, sortIcon } from "@/components/helpers";

import { WorkOrderSimple } from "@/store/modules/workOrders";
import { prettifyWorkOrderStatus } from "@/models/WorkOrder";
import BarPercentageChart from "@/components/BarPercentageChart/BarPercentageChart.vue";

import StatusIndicator from "@/components/StatusIndicator/StatusIndicator.vue";

import {
  debouncedFilteredWorkOrders,
  apiWorkOrders,
  workOrders,
} from "@/components/WorkOrders/work-orders-shared";

import {
  arrowUpHandler,
  arrowDownHandler,
  filterOnInputHandler,
} from "@/utilities/pickListHelpers";

export default defineComponent({
  name: "workOrders",
  props: {
    useMode: {
      type: String,
      required: false,
    },
  },
  components: {
    BarPercentageChart,
    LoadingAnimation,
    ListPageHeader,
    StatusIndicator,
  },

  setup(props, ctx) {
    const $store = ctx.root.$store;

    const dataLoaded = ref(false);
    const loadingMessage =
      "Loading work orders...Dragging them in from the database...";

    const firstInput = ref<null | HTMLInputElement>(null);

    const showMenu = ref(false);

    const toggleMenu = (newState: boolean) => {
      showMenu.value = newState;
      if (!newState) {
        ctx.root.$nextTick(() => {
          if (firstInput.value) {
            firstInput.value.focus();
          }
        });
      }
    };

    let currentStatuses = ["pending", "queued", "completed"];

    const filter = ref("");

    const clearFilter = () => {
      filterOnInputHandler(
        apiWorkOrders,
        workOrders,

        "",
        filter,
        debouncedFilteredWorkOrders,

        sortKey,
        sortAsc,
        updateSort,

        selectedIndex,
      );
    };

    const sortKey = ref<keyof WorkOrderSimple>("name");
    const sortAsc = ref(true);

    const showRouteBuilder = (id: string) => {
      return itemRouteBuilder(NamedRts.workOrderTasks, id);
    };

    const editRouteBuilder = (id: string) => {
      return itemRouteBuilder(NamedRts.buildWorkOrder, id);
    };

    const newRouteBuilder = (id: string) => {
      return itemRouteBuilder(NamedRts.workOrderEditForm, id);
    };

    const newFromTemplateRouteBuilder = (id: string) => {
      return itemRouteBuilder(NamedRts.workOrderCreateForm, id);
    };

    const localSortIcon = (lSortKey: keyof WorkOrderSimple) => {
      return sortIcon(sortAsc, sortKey, lSortKey);
    };

    const getWorkOrdersByStatus = (statuses: string[]) => {
      currentStatuses = [...statuses];
      dataLoaded.value = false;
      const jsConn = new JSConn();
      jsConn.formParams = {
        statuses: statuses,
      };
      return $store.dispatch("needWorkOrders", jsConn).then(() => {
        workOrders.value = [...apiWorkOrders.value];
        // flip the sort, otherwise on load it would be off
        sortAsc.value = !sortAsc.value;
        updateSort(sortKey.value);
        dataLoaded.value = true;
        return Promise.resolve();
      });
    };

    const queueWorkOrder = (wo: WorkOrderSimple) => {
      const jsConn = new JSConn();

      const td = format(new Date(), "yyyy-MM-dd");
      jsConn.formParams = {
        id: wo.id,
        start_date: td,
      };
      $store.dispatch("queueWorkOrder", jsConn).then(() => {
        ctx.root.$bvToast.toast("Work Order Queued", TOAST_OPTIONS);
        // load all the work orders again
        // very likely a more efficient way to do this
        // but this is the least amount of code and the quickest at this time
        // 2019-03-27
        return getWorkOrdersByStatus(currentStatuses);
      });
    };

    const updateSort = (lSortKey: keyof WorkOrderSimple) => {
      workOrders.value = genericSort(
        [...workOrders.value],
        sortAsc,
        sortKey,
        lSortKey,
      );
    };

    onMounted(() => {
      getWorkOrdersByStatus(currentStatuses).finally(() => {
        if (props.useMode !== "pickerMode") {
          ctx.root.$nextTick(() => {
            if (firstInput.value) {
              firstInput.value.focus();
            }
          });
        }
      });
    });

    // this is for when the work orders are used as a picker
    // they make the arrow keys work correctly
    const selectedIndex = ref(-1);

    const onArrowDown = (evt: any) => {
      evt.preventDefault();
      arrowDownHandler(selectedIndex, workOrders.value.length);
    };

    const onArrowUp = (evt: any) => {
      evt.preventDefault();
      arrowUpHandler(selectedIndex);
    };

    const filterOnInput = ({ target }: { target: HTMLInputElement }) => {
      filterOnInputHandler(
        apiWorkOrders,
        workOrders,

        target.value,
        filter,
        debouncedFilteredWorkOrders,

        sortKey,
        sortAsc,
        updateSort,

        selectedIndex,
      );
    };

    const filterOnEnter = (evt: any) => {
      evt.preventDefault();
      let tmpResult = workOrders.value[selectedIndex.value];

      if (!tmpResult) {
        tmpResult = workOrders.value[0];
      }

      if (props.useMode === "pickerMode") {
        sendWorkOrder(tmpResult);
      } else {
        ctx.root.$router.push(showRouteBuilder(tmpResult.id));
      }
    };

    const sendWorkOrder = (il: WorkOrderSimple) => {
      ctx.emit("sendWorkOrder", il);
    };

    return {
      dataLoaded,
      loadingMessage,

      workOrders,

      queueWorkOrder,

      filter,
      filterOnEnter,
      filterOnInput,
      clearFilter,

      getWorkOrdersByStatus,
      updateSort,
      localSortIcon,
      showRouteBuilder,
      editRouteBuilder,
      newRouteBuilder,
      newFromTemplateRouteBuilder,

      prettifyWorkOrderStatus,

      onArrowDown,
      onArrowUp,
      selectedIndex,
      sendWorkOrder,

      showMenu,
      toggleMenu,
      firstInput,
    };
  },
});
